import { useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import type LenisType from 'lenis';
import { useReducedMotion } from './useReducedMotion.js';

/**
 * useLenis — buttery, physics-based smooth scrolling (the foundation every
 * award site is built on), wired into GSAP's ticker so ScrollTrigger pins and
 * scrubs stay perfectly in sync. Wheel/keys are smoothed; touch stays native
 * (Lenis default) so phones keep their momentum. Disabled under reduced-motion.
 *
 * Scope it to the page that wants it (mount in that page's component) so app
 * surfaces with their own scroll-locking (modals, lightboxes) are untouched.
 */
export function useLenis(): void {
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) return;
    let destroyed = false;
    let lenis: LenisType | null = null;
    let frame: ((time: number) => void) | null = null;

    void (async () => {
      const Lenis = (await import('lenis')).default;
      if (destroyed) return;
      lenis = new Lenis({ duration: 1.05, smoothWheel: true });
      lenis.on('scroll', () => ScrollTrigger.update());
      frame = (time: number): void => {
        lenis?.raf(time * 1000);
      };
      gsap.ticker.add(frame);
      gsap.ticker.lagSmoothing(0);
    })();

    return () => {
      destroyed = true;
      if (frame) gsap.ticker.remove(frame);
      gsap.ticker.lagSmoothing(500, 33);
      lenis?.destroy();
    };
  }, [reduced]);
}
